/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button, TextField, useTheme } from '@material-ui/core';
import { login, autoLogin } from 'src/actions';
import PluginsSupport from '../Presentation/PluginsSupport';

const schema = {
  cardNumber: {
    presence: { allowEmpty: false, message: 'is required' },
  },
  password: {
    presence: { allowEmpty: false, message: 'is required' }
  }
};

const useStyles = makeStyles((theme) => ({
  root: {},
  fields: {
    margin: theme.spacing(-1),
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      flexGrow: 1,
      margin: theme.spacing(1)
    },
    '& > input': {
      backgroundColor: theme.config.layout.primary.contrastText,
    }
  },
  submitButton: {
    marginTop: theme.spacing(2),
    ...theme.config.layout.button
  },
}));

function LoginForm({ className, ...rest }) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isAuth = useSelector((state) => state.session.loggedIn);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const loadSession = () => {
    const userSessionJson = localStorage.getItem('h07_lng');
    if (userSessionJson) {
      const userSession = JSON.parse(userSessionJson);
      dispatch(autoLogin(userSession));
    }
  };

  useEffect(() => {
    if (!isAuth) loadSession();
  }, []); // eslint-disable-line

  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {}
  });

  const handleChange = (event) => {
    event.persist();

    setFormState((prevFormState) => ({
      ...prevFormState,
      values: {
        ...prevFormState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value
      },
      touched: {
        ...prevFormState.touched,
        [event.target.name]: true
      }
    }));
  };

  const handleVerify = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch(login({
      username: document.querySelector('[name="cardNumber"]').value,
      password: document.querySelector('[name="password"]').value,
      'g-recaptcha-response': recaptchaToken
    }));
  };

  const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState((prevFormState) => ({
      ...prevFormState,
      isValid: !errors,
      errors: errors || {}
    }));
  }, [formState.values]);

  useEffect(() => {
    if (isAuth) {
      const dataRedir = localStorage.getItem('redirect');
      if (dataRedir && (dataRedir.match(/redirect/g) || []).length === 1) {
        localStorage.removeItem('redirect');
        let redirPieces = dataRedir.split('redirect/');
        const tRedirPieces = redirPieces[1].split('/');
        redirPieces = redirPieces[1].replace(tRedirPieces[0], '');
        // salvare in qualche modo i parametri del redirect di drive "redirpieces"
        // una url dovrebbe ssere composta così: http://host/redirect/drive/chairs-savanna
        // la prima chiave dopo "redirect" è il componente da montare via routing
        localStorage.setItem(tRedirPieces[0], tRedirPieces[tRedirPieces.length - 1]);
        history.push(`/${tRedirPieces[0]}`);
      } else {
        history.push('/');
      }
    }
  }, [isAuth, history]);

  return (
    <>
      <form
        {...rest}
        className={clsx(classes.root, className)}
        onSubmit={handleSubmit}
      >
        <div className={classes.fields}>
          <TextField
            error={hasError('cardNumber')}
            helperText={
              hasError('cardNumber') ? formState.errors.cardNumber[0] : null
            }
            label="Username"
            name="cardNumber"
            onChange={handleChange}
            value={formState.values.cardNumber || ''}
            variant="outlined"
            className={`${classes.fieldOverride} halfBlock clearRight`}
          />
          <TextField
            error={hasError('password')}
            fullWidth
            helperText={
              hasError('password') ? formState.errors.password[0] : null
            }
            label="Password"
            name="password"
            onChange={handleChange}
            type="password"
            value={formState.values.password || ''}
            variant="outlined"
            className={`${classes.fieldOverride} halfBlock clearRight`}
          />
        </div>
        <Button
          className={classes.submitButton}
          color="secondary"
          disabled={!formState.isValid}
          size="large"
          type="submit"
          variant="contained"
        >
          {theme.config.strings.loginLabel}
        </Button>
      </form>
      <GoogleReCaptcha onVerify={(token) => handleVerify(token)} />
    </>
  );
}

LoginForm.propTypes = {
  className: PropTypes.string
};

export default LoginForm;
